/**
 * Unified Size & Style Constants for Modals and Sheets
 *
 * This file provides standardized sizing, spacing, and styling constants
 * to ensure consistency across all modal and sheet implementations.
 *
 * @module modal-sheet-constants
 */

/**
 * Modal size constants (max-width)
 * Use these for consistent modal sizing across the application
 *
 * @example
 * ```tsx
 * <BaseModal size="lg" title="Edit Form">
 *   <FormContent />
 * </BaseModal>
 * ```
 */
export const MODAL_SIZES = {
  xs: 'max-w-xs',     // 320px - Tiny confirmations
  sm: 'max-w-sm',     // 384px - Simple dialogs
  md: 'max-w-md',     // 448px - Basic forms
  lg: 'max-w-lg',     // 512px - Standard forms
  xl: 'max-w-xl',     // 576px - Detailed forms
  '2xl': 'max-w-2xl', // 672px - Complex forms
  '3xl': 'max-w-3xl', // 768px - Preview/comparison
  '4xl': 'max-w-4xl', // 896px - Data-rich views
  '5xl': 'max-w-5xl', // 1024px - Full-featured
  '6xl': 'max-w-6xl', // 1152px - Maximum width
  full: 'max-w-full', // 100% - Special cases
} as const;

/**
 * Sheet width constants
 * Use these for consistent sheet sizing from the right side
 *
 * @example
 * ```tsx
 * <BaseSheet width="lg" title="Details Panel">
 *   <DetailContent />
 * </BaseSheet>
 * ```
 */
export const SHEET_WIDTHS = {
  narrow: 'w-full sm:w-[280px]',   // Side panels, lists
  sm: 'w-full sm:w-[400px]',       // Compact forms
  md: 'w-full sm:w-[50vw]',        // Medium content
  lg: 'w-full sm:w-[60vw]',        // Standard sheets
  xl: 'w-full sm:w-[75vw]',        // Detailed views
  xxl: 'w-full sm:w-[80vw]',       // Maximum width
  full: 'w-full',                  // Full screen
} as const;

/**
 * Modal height constraints
 * Use these for controlling vertical space in modals
 *
 * @example
 * ```tsx
 * <BaseModal size="lg" height="md" title="Scrollable Content">
 *   <LongForm />
 * </BaseModal>
 * ```
 */
export const MODAL_HEIGHTS = {
  auto: '',                         // Auto height (content-based)
  sm: 'max-h-[50vh] min-h-[400px]', // 50% viewport height, min 400px
  md: 'max-h-[70vh] min-h-[500px]', // 70% viewport height, min 500px
  lg: 'max-h-[80vh] min-h-[600px]', // 80% viewport height, min 600px
  xl: 'max-h-[85vh] min-h-[700px]', // 85% viewport height, min 700px
  full: 'h-[90vh]',                 // 90% viewport height (fixed)
} as const;

/**
 * Standardized footer classes for consistent styling
 * Apply these to modal/sheet footers for uniform appearance
 * 
 * Based on audiences best practices:
 * - Modal: border-top with padding, flex layout for buttons
 * - Sheet: border-top with padding, flex-shrink-0 to prevent compression
 */
export const FOOTER_CLASSES = {
  modal: 'border-t border-gray-200 pt-4',
  sheet: 'border-t border-slate-200 px-responsive py-4 flex-shrink-0',
} as const;

/**
 * Standardized header classes for consistent styling
 * Apply these to modal/sheet headers for uniform appearance
 * 
 * Based on audiences best practices:
 * - Modal: border-bottom with padding
 * - Sheet: padding + border-bottom, flex-shrink-0 to prevent compression
 */
export const HEADER_CLASSES = {
  modal: 'border-b border-gray-200 pb-4',
  sheet: 'px-responsive py-4 border-b border-slate-200 flex-shrink-0',
} as const;

/**
 * Spacing constants for modal/sheet content
 */
export const SPACING = {
  modalPadding: 'p-6',
  sheetPadding: 'px-responsive py-5',
  titleMargin: 'mb-2',
  descriptionMargin: 'mt-1',
} as const;

/**
 * Animation duration for open/close transitions
 */
export const ANIMATION_DURATION = {
  modal: 200,  // milliseconds
  sheet: 300,  // milliseconds
} as const;

// Type exports for TypeScript
export type ModalSize = keyof typeof MODAL_SIZES;
export type SheetWidth = keyof typeof SHEET_WIDTHS;
export type ModalHeight = keyof typeof MODAL_HEIGHTS;
